import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  useTheme,
  Snackbar,
  Alert,
} from '@mui/material';

import ActionBar from '../components/ActionBar';
import AlertBar from '../components/AlertBar';
import AdminBreadcrumb from '../components/AdminBreadcrumb';
import AdminUserListTable from '../components/AdminUserListTable';
import Footer from '../components/Footer';
import AbstractBackground from '../components/AbstractBackground';

import actionBarConfig from '../config/actionBarConfig';

import {
  addMessage,
  clearMessages,
  selectChatContext,
} from '../reducers/messagesReducer';

import {
  setShowTiles,
  setIsActiveChat,
  setSelectedTileId,
  clearSelectedTileId,
  addPromptItem,
  removePromptItem,
  updatePromptItem,
  clearPromptItems
} from '../reducers/uiReducer';

import {
  setLoading
} from '../reducers/loadingReducer';

import {
  hideToast,
  showToast
} from '../reducers/uiReducer';

import {
  setAdminUserList,
  setSelectedUser
} from '../reducers/adminReducer';

import { fetchUserList } from '../services/apiService';
import useResponsive from '../hooks/useResponsive';

const UsersManager = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const loading = useSelector((state) => state.loading);
  const toast = useSelector((state) => state.ui.toast);
  const admin = useSelector((state) => state.admin);
  const containerRef = useRef(null);
  const { isTabletScreen, isMobileScreen } = useResponsive();

  const handleNewChat = () => {
    dispatch(clearMessages());
    dispatch(clearSelectedTileId());
    dispatch(setShowTiles(true));
    dispatch(setIsActiveChat(false));
    dispatch(clearPromptItems());
  };

  const handleSelectedRow = (userId) => {
    dispatch(setSelectedUser({ userId }));
    navigate(`/admin/users/${userId}`);
  };

  const memoizedBackground = useMemo(
    () => <AbstractBackground animate={loading} containerRef={containerRef} />,
    [loading]
  );

  const breadcrumbPath = [
    { label: 'Admin Dashboard', link: '/admin' },
    { label: 'Manage Users' },
  ];

  useEffect(() => {
    dispatch(setLoading(true));
    fetchUserList()
      .then((response) => {
        dispatch(setAdminUserList(response));
        dispatch(setLoading(false));
      })
      .catch((error) => {
        dispatch(showToast({ message: error.message, severity: 'error' }));
        dispatch(setLoading(false));
      });
  }, [dispatch]);

  return (
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <Box
        sx={{
          height: '100vh',
          width: '100%',
          minWidth: 300,
          maxWidth: 1000,
          display: 'flex',
          flexDirection: 'column',
        }}
        ref={containerRef}
      >
        {memoizedBackground}

        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <ActionBar config={actionBarConfig} onNewChat={handleNewChat} />
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            overflow: 'hidden',
            display: 'flex',
            background: theme.palette.mode === 'dark'
                            ? 'linear-gradient(to top,rgba(18, 18, 18, 0.8),rgba(29, 29, 29, 0.6))'
                            : 'linear-gradient(to top, rgba(255,255,255,0.2), rgba(0,116,211,0.1))',
            boxShadow: 1,
            position: 'relative',
            overflowY: 'auto',
          }}
        >
          <AlertBar
            open={toast.open}
            onClose={() => dispatch(hideToast())}
            severity={toast.severity}
            message={toast.message}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              padding: 2,
              margin: 2,
              borderRadius: 2,
              backgroundColor: theme.palette.background.paper,
              boxShadow: 1,
              alignSelf: 'flex-start',
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(180deg, #2d2d2d, #1e1e1e)'
                : 'linear-gradient(180deg, #ffffff, #e4f2fb, #f2f2f2)',
            }}
          >
            <AdminBreadcrumb path={breadcrumbPath} />
            <AdminUserListTable
              userList={admin.userList}
              handleSelectedRow={handleSelectedRow}
            />
          </Box>
        </Box>

        <Footer
          sx={{
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            backgroundColor: theme.palette.background.default,
          }}
        />
      </Box>
    </Box>
  );
};

export default UsersManager;
